'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import RelatedBlogCarousel from '@/components/RelatedBlogCarousel';

export default function BlogPost() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    // Initialize particles only on client side
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-4">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle, i) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              From Shared to Dedicated Hosting: <span className="text-transparent bg-clip-text bg-gradient-rainbow">Which Service is Right for You?</span>
            </motion.h1>
            <motion.div 
              className="text-xl text-slate-300 max-w-3xl mx-auto flex items-center justify-center gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <p>By 1 Dollar Cloud | Published On: November 24, 2024 | Categories: Web Hosting</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Illustration */}
      <section className="py-12 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <img
                      src="/assets/blog-images/hosting-types-comparison.svg"
                      alt="Different Types of Hosting Services Illustration"
                      className="w-full h-[500px] object-contain"
                    />
        </div>
      </section>

      {/* Blog Content */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="prose prose-invert max-w-none"
          >
            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <p className="text-slate-300">Choosing the right hosting service is crucial for your website's success. Whether you're launching a personal blog or running an enterprise-level e-commerce platform, understanding the different types of hosting services available can help you make an informed decision. In this comprehensive guide, we'll explore the various hosting options offered by 1 Dollar Cloud and help you determine which one best suits your needs.</p>
            </div>

            {[
              {
                title: "Understanding Different Hosting Types",
                content: [
                  "Before diving into specific recommendations, let's understand the main types of hosting services available:",
                  "• **Shared Hosting**: Multiple websites share server resources",
                  "• **WordPress Hosting**: Optimized specifically for WordPress sites",
                  "• **VPS Hosting**: Virtual Private Server with dedicated resources",
                  "• **Dedicated Hosting**: Entire server dedicated to one user"
                ]
              },
              {
                title: "Shared Hosting: Perfect for Beginners",
                content: [
                  "**Best For**: Small websites, blogs, and personal projects",
                  "**Key Features**:",
                  "• Cost-effective solution starting at $1/month",
                  "• Easy to manage with cPanel interface",
                  "• Includes essential features like SSL certificates",
                  "• Suitable for up to 10,000 monthly visitors",
                  "**Limitations**:",
                  "• Shares server resources with other websites",
                  "• Limited customization options",
                  "• May experience slower performance during peak times"
                ]
              },
              {
                title: "WordPress Hosting: Optimized for CMS",
                content: [
                  "**Best For**: WordPress websites and blogs",
                  "**Key Features**:",
                  "• Pre-installed WordPress",
                  "• Automatic updates and backups",
                  "• WordPress-specific security features",
                  "• Optimized for WordPress performance",
                  "**Why Choose WordPress Hosting**:",
                  "• Faster loading times for WordPress sites",
                  "• Enhanced security measures",
                  "• Expert WordPress support"
                ]
              },
              {
                title: "VPS Hosting: The Middle Ground",
                content: [
                  "**Best For**: Growing businesses and medium-sized websites",
                  "**Key Features**:",
                  "• Dedicated virtual resources",
                  "• Root access for customization",
                  "• Scalable resources",
                  "• Suitable for up to 100,000 monthly visitors",
                  "**Advantages**:",
                  "• Better performance than shared hosting",
                  "• More control over server environment",
                  "• Guaranteed resources"
                ]
              },
              {
                title: "Dedicated Hosting: Maximum Power",
                content: [
                  "**Best For**: Large businesses and high-traffic websites",
                  "**Key Features**:",
                  "• Full server resources",
                  "• Complete control over configuration",
                  "• Maximum security options",
                  "• Suitable for 100,000+ monthly visitors",
                  "**Benefits**:",
                  "• Highest performance levels",
                  "• Enhanced security",
                  "• Custom server configurations"
                ]
              },
              {
                title: "How to Choose the Right Hosting",
                content: [
                  "Consider these factors when selecting your hosting service:",
                  "1. **Budget**: Start with shared hosting if cost is a primary concern",
                  "2. **Traffic**: Estimate your monthly visitors",
                  "3. **Technical Expertise**: Consider your ability to manage servers",
                  "4. **Growth Plans**: Choose a service that can scale with your needs",
                  "5. **Security Requirements**: Assess your security needs"
                ]
              },
              {
                title: "Making the Transition",
                content: [
                  "As your website grows, you may need to upgrade your hosting service. Here's when to consider upgrading:",
                  "• **Shared to VPS**: When experiencing regular slowdowns or reaching resource limits",
                  "• **VPS to Dedicated**: When requiring full server resources or maximum customization",
                  "At 1 Dollar Cloud, we make these transitions smooth with our migration assistance service."
                ]
              }
            ].map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-slate-800/50 rounded-xl p-8 mb-8"
              >
                <h2 className="text-2xl font-bold text-white mb-6">{section.title}</h2>
                {section.content.map((paragraph, i) => (
                  <p key={i} className="text-slate-300 mb-4" dangerouslySetInnerHTML={{ __html: paragraph.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }}></p>
                ))}
              </motion.div>
            ))}

            <div className="bg-slate-800/50 rounded-xl p-8 mb-8">
              <h2 className="text-2xl font-bold text-white mb-6">Conclusion</h2>
              <p className="text-slate-300 mb-4">Choosing the right hosting service is a crucial decision that can significantly impact your website's success. At 1 Dollar Cloud, we offer a complete range of hosting solutions to meet your needs at every stage of your online journey.</p>
              <p className="text-slate-300 mb-4">Whether you're starting with shared hosting or ready for a dedicated server, our team is here to help you make the right choice and ensure a smooth hosting experience.</p>
              <p className="text-slate-300">Remember, the best hosting solution is one that balances your current needs with future growth potential while staying within your budget.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Blog Carousel */}
      <section className="py-8 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <RelatedBlogCarousel currentSlug="from-shared-to-dedicated-hosting-which-service-is-right-for-you-and-why" />
          
        </div>
      </section>
    </div>
  );
}